import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, Banner, LoadingSpinner, Modal } from '@/components/ui';
import { useUsers, dismissGame, useAppConfig } from '@/hooks/useShared';
import { calculateSettlement, settleAllTransactions, markAllTransactionsSettled, useSettlementTransactions, toggleTransactionSettled } from '@/hooks/useSettlement';
import { useAuth } from '@/hooks/useAuth';
import NotificationService from '@/services/notification.service';
import type { Game } from '@/lib/types';
import { formatCurrency, roundTo50Cents } from '@/lib/utils';
import { canManageUsers } from '@/lib/roles';
import { buildBeemPaymentUrl } from '@/utils/beem';

interface DigitalTransfersStepProps {
  game: Game;
  onBack: () => void;
  onPublish: (includeEmail: boolean, transactions?: any[]) => void;
}


export function DigitalTransfersStep({ game, onBack, onPublish }: DigitalTransfersStepProps) {
  const { user } = useAuth();
  const { users } = useUsers();
  const { config } = useAppConfig();
  const { transactions, loading: txLoading } = useSettlementTransactions(game.id);
  const [calculating, setCalculating] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [settlingTxId, setSettlingTxId] = useState<string | null>(null);
  const [nudgingTxId, setNudgingTxId] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [markingAllSettled, setMarkingAllSettled] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [breakdownExpanded, setBreakdownExpanded] = useState(false);
  // Email feature hidden until domain verification is resolved

  const isAdmin = canManageUsers(user);

  const handleCalculate = useCallback(async () => {
    setCalculating(true);
    setError(null);
    try {
      await calculateSettlement(game.id, user?.id ?? '');
    } catch (err) {
      console.error('Failed to calculate settlement:', err);
      setError('Failed to calculate transfers.');
    } finally {
      setCalculating(false);
    }
  }, [game.id, user?.id]);

  const hasAutoCalculated = useRef(false);

  useEffect(() => {
    if (txLoading || calculating) return;
    if (transactions.length > 0) return;

    const totalCashIn = game.settlement?.player_snapshot?.reduce((sum, p) => sum + (p.cashIn || 0), 0) || 0;
    if (totalCashIn > 0) return; // Cash games go through CashDistributionStep which triggers calculation

    if (hasAutoCalculated.current) return;
    hasAutoCalculated.current = true;

    handleCalculate();
  }, [txLoading, calculating, transactions.length, game, handleCalculate]);

  const [settledManualIds, setSettledManualIds] = useState<Set<string>>(new Set());

  const handleToggleSettled = async (txId: string, currentStatus: string) => {
    if (!isAdmin) return;
    
    if (String(txId).startsWith('manual-')) {
      const newSet = new Set(settledManualIds);
      if (newSet.has(txId)) newSet.delete(txId);
      else newSet.add(txId);
      setSettledManualIds(newSet);
      return;
    }

    setSettlingTxId(txId);
    try {
      await toggleTransactionSettled(txId, user?.id || 'spectator', currentStatus === 'settled');
    } catch (err) {
      setError('Failed to update transaction.');
    } finally {
      setSettlingTxId(null);
    }
  };

  const handleGameSettled = async () => {
    if (!isAdmin) return;
    setFinalizing(true);
    setError(null);
    try {
      await settleAllTransactions(game.id, user?.id ?? '');
      await dismissGame(game.id);
    } catch (err: any) {
      console.error('Failed to settle game:', err);
      setError(err?.message || 'Failed to settle game.');
    } finally {
      setFinalizing(false);
    }
  };

  const handleBeem = (payeeBeemId: string | null, amount: number) => {
    if (!payeeBeemId) return;
    const url = buildBeemPaymentUrl(payeeBeemId, amount);
    window.open(url, '_blank');
  };

  const handleNudge = async (txId: string) => {
    if (!isAdmin) return;
    setNudgingTxId(txId);
    try {
      const activeChatConfig = config?.telegram_chat_configs?.find(
        c => c.id === config?.telegram_active_chat_id
      );
      const activeChatId = activeChatConfig ? Number(activeChatConfig.chat_id) : undefined;
      if (activeChatId === undefined) {
        return;
      }

      await NotificationService.sendEvent({
        type: 'TX_NUDGE',
        payload: {
          transactionId: txId,
          gameId: game.id,
          gameNumber: import.meta.env.DEV
            ? `DEV-${String(game.gameNumber).padStart(3, '0')}`
            : String(game.gameNumber).padStart(3, '0'),
          chatId: activeChatId,
        },
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to send nudge.');
    } finally {
      setNudgingTxId(null);
    }
  };

  const handlePublishClick = async () => {
    setPublishing(true);
    try {
      await onPublish(false, transactions);
    } catch {
      // Error already handled by parent (setError in SettlementPage)
    } finally {
      setPublishing(false);
    }
  };

  const calculationLog = useMemo(() => {
    const pot = game.settlement?.player_snapshot.reduce((sum, p) => sum + p.buyIn, 0) || 0;
    const cash = game.settlement?.player_snapshot.reduce((sum, p) => sum + (p.cashIn || 0), 0) || 0;
    const digital = game.settlement?.player_snapshot.reduce((sum, p) => sum + p.digitalIn, 0) || 0;
    
     const playersData = game.players.filter(p => !p.is_spectator).map(p => {
       const name = users.find(u => u.id === p.user_id)?.name || p.name;
        const profit = (game.settlement?.player_inputs[p.user_id]?.final_stack || 0) - (p.total_bought_in || 0);
         const adjustedNet = profit + (p.cash_in || 0) - (game.settlement?.player_inputs[p.user_id]?.cash_received || 0);
         return {
           id: p.user_id,
           name,
           left_early: p.left_early,
           stack: game.settlement?.player_inputs[p.user_id]?.final_stack || 0,
          totalBoughtIn: p.total_bought_in || 0,
          cashIn: p.cash_in || 0,
          digitalIn: p.digital_in || 0,
          cash_received: game.settlement?.player_inputs[p.user_id]?.cash_received || 0,
          profit,
          settlement: adjustedNet
        };
     });
    
    return { pot, cash, digital, players: playersData };
  }, [game, users]);

  const manualTransfers = useMemo(() => {
    if (transactions.length > 0) return null;
    
    const debtors = calculationLog.players
      .filter(p => p.settlement < -0.01)
      .map(p => ({ ...p, remaining: Math.abs(p.settlement) }))
      .sort((a, b) => b.remaining - a.remaining);
      
    const creditors = calculationLog.players
      .filter(p => p.settlement > 0.01)
      .map(p => ({ ...p, remaining: p.settlement }))
      .sort((a, b) => b.remaining - a.remaining);
      
    const results: any[] = [];
    let di = 0, ci = 0;
    const dCopy = JSON.parse(JSON.stringify(debtors));
    const cCopy = JSON.parse(JSON.stringify(creditors));

    while (di < dCopy.length && ci < cCopy.length) {
      const d = dCopy[di], c = cCopy[ci];
      const minRem = Math.min(d.remaining, c.remaining);
      const amt = roundTo50Cents(minRem);
      if (amt > 0.01) {
        results.push({
          id: 'manual-' + di + '-' + ci,
          from_user_id: d.id,
          to_user_id: c.id,
          amount: amt,
          status: 'pending'
        });
        d.remaining -= amt;
        c.remaining -= amt;
      } else {
        // Cannot make a transfer of at least 0.50. 
        // The player with the smaller remaining balance is effectively exhausted.
        if (d.remaining < c.remaining) di++;
        else if (c.remaining < d.remaining) ci++;
        else { di++; ci++; }
      }
      if (di < dCopy.length && dCopy[di] && dCopy[di].remaining < 0.01) di++;
      if (ci < cCopy.length && cCopy[ci] && cCopy[ci].remaining < 0.01) ci++;
    }
    return results;
  }, [calculationLog, transactions]);

  const displayTransactions = useMemo(() => {
    return transactions.length > 0 ? transactions : (manualTransfers || []);
  }, [transactions, manualTransfers]);

  const allSettled = useMemo(() => {
    if (displayTransactions.length === 0) return true;
    return displayTransactions.every((tx: any) => {
      const isManual = String(tx.id).startsWith('manual-');
      return isManual ? settledManualIds.has(tx.id) : tx.status === 'settled';
    });
  }, [displayTransactions, settledManualIds]);

  const handleMarkAllSettled = async () => {
    if (!isAdmin) return;
    setMarkingAllSettled(true);
    setError(null);
    try {
      if (transactions.length > 0) {
        await markAllTransactionsSettled(game.id, user?.id ?? '');
      } else {
         const allIds = displayTransactions.map((tx: any) => tx.id);
        setSettledManualIds(new Set(allIds));
      }
    } catch (err) {
      console.error('Failed to mark all as settled:', err);
      setError('Failed to mark transfers as settled.');
    } finally {
      setMarkingAllSettled(false);
    }
  };

  if (txLoading || calculating) {
    return <LoadingSpinner message={calculating ? "Calculating Transfers..." : "Loading transfers..."} />;
  }

  return (
    <div>
      <div className="flex justify-between items-center gap-2 mb-2">
        {displayTransactions.length > 0 && (
          <button
            onClick={handlePublishClick}
            disabled={markingAllSettled || publishing}
            style={{ flex: 1, background: '#006855', border: 'none', color: 'white', padding: '5px 4px', borderRadius: 10, fontSize: 9, fontWeight: 800, textTransform: 'uppercase', cursor: (markingAllSettled || publishing) ? 'not-allowed' : 'pointer', textAlign: 'center', boxShadow: '0 4px 8px rgba(0,104,85,0.2)', opacity: (markingAllSettled || publishing) ? 0.5 : 1 }}
          >
            {publishing ? 'Publishing...' : 'Publish'}
          </button>
        )}
        <button
          onClick={handleMarkAllSettled}
          disabled={markingAllSettled}
          style={{ flex: 1, background: 'white', border: '1px solid #006855', color: '#006855', padding: '5px 4px', borderRadius: 10, fontSize: 9, fontWeight: 800, textTransform: 'uppercase', cursor: 'pointer', textAlign: 'center' }}
        >
          {markingAllSettled ? 'Settling...' : 'Settle All'}
        </button>
        <button
          onClick={() => setShowLog(true)}
          style={{ flex: 1, background: 'white', border: '1px solid #e5e9ea', padding: '5px 4px', borderRadius: 10, fontSize: 9, fontWeight: 800, color: '#595c5d', textTransform: 'uppercase', cursor: 'pointer', textAlign: 'center' }}
        >
          Detailed Log
        </button>
      </div>

      {error && <Banner type="error" className="mb-4">{error}</Banner>}

      {displayTransactions.length === 0 ? (
        <div>
          <Card className="p-8 text-center bg-[#e8f5f0] border-none shadow-none">
            <span className="text-3xl mb-3 block">🎉</span>
            <div className="text-lg font-bold text-[#006855]">All Square!</div>
            <div className="text-sm text-[#006855] opacity-70 font-medium mt-1">
              No digital transfers required.
            </div>
          </Card>
        </div>
      ) : (
        <div className="space-y-2">
           {displayTransactions.map((tx: any) => {
            const fromUser = users.find(u => u.id === tx.from_user_id);
            const fromPlayer = game.players.find(p => p.user_id === tx.from_user_id);
            const toUser = users.find(u => u.id === tx.to_user_id);
            const toPlayer = game.players.find(p => p.user_id === tx.to_user_id);
            
            const fromName = fromUser?.name || fromPlayer?.name || 'Payer';
            const toName = toUser?.name || toPlayer?.name || 'Payee';
            
            const isManual = String(tx.id).startsWith('manual-');
            const isSettledStatus = isManual ? settledManualIds.has(tx.id) : tx.status === 'settled';

            return (
              <div
                key={tx.id}
                aria-label={`${fromName} pays ${toName} ${formatCurrency(tx.amount)}`}
                className={`w-full max-w-full min-w-0 box-border rounded-xl p-3 mb-1.5 border flex flex-col ${
                  isSettledStatus
                    ? 'bg-[#f0f2f3] border-[#d0d5d7] opacity-[0.45]'
                    : 'bg-white border-[#e5e9ea]'
                }`}
              >
                {/* Row 1: Amount + Names */}
                <div className="flex items-center gap-3">
                  {/* Amount */}
                  <div className="bg-[#e8f5f0] rounded-xl px-3 py-2.5 min-w-[70px] text-center flex-shrink-0">
                    <div className="text-base font-extrabold text-[#006855] whitespace-nowrap">
                      {formatCurrency(tx.amount)}
                    </div>
                  </div>

                  {/* Names */}
                  <div className="flex-1 flex items-center gap-2 min-w-0">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-[#2c2f30] overflow-hidden text-ellipsis whitespace-nowrap">
                        {fromName}
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-[10px] font-semibold text-[#595c5d]">Paid:</span>
                        <span className="text-xs">{tx.payer_confirmed ? '✅' : '⏳'}</span>
                      </div>
                    </div>

                    <span className="material-symbols-outlined text-[#006855] text-lg flex-shrink-0 opacity-50">arrow_forward</span>

                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-[#2c2f30] overflow-hidden text-ellipsis whitespace-nowrap">
                        {toName}
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-[10px] font-semibold text-[#595c5d]">Received:</span>
                        <span className="text-xs">{tx.payee_confirmed ? '✅' : '⏳'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 2: Pay + Actions */}
                <div className="flex items-center justify-between mt-2 pt-2 border-t border-[#f0f2f3]">
                  {/* Pay button - visible to all */}
                  {toUser?.beem_id && !isSettledStatus ? (
                    <button
                      onClick={() => handleBeem(toUser.beem_id, tx.amount)}
                      className="text-center text-[11px] font-bold text-white py-1.5 px-4 rounded-lg bg-[#006855] hover:bg-[#005b4a] transition-colors cursor-pointer border-none"
                      aria-label={`Pay ${toName} with Beem`}
                      title="Pay with Beem"
                    >
                      Pay
                    </button>
                  ) : (
                    <span
                      className="text-center text-[11px] font-bold text-[#8a8d8e] py-1.5 px-4 rounded-lg bg-[#d0d5d7] cursor-not-allowed"
                      aria-label={isManual ? `Manual transfer cannot use Beem for ${toName}` : `No Beem ID set for ${toName}`}
                      aria-disabled="true"
                      title="No Beem ID set"
                    >
                      Pay
                    </span>
                  )}

                  {/* Actions - admin only */}
                  {isAdmin && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleNudge(tx.id)}
                        disabled={nudgingTxId === tx.id || !fromUser?.telegram_id}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center border-none cursor-pointer transition-colors ${
                          !fromUser?.telegram_id
                            ? 'bg-[#f0f2f3] text-[#c5c9ca] cursor-not-allowed opacity-50'
                            : 'bg-[#e8f5f0] text-[#006855] hover:bg-[#006855] hover:text-white'
                        }`}
                        aria-label={!fromUser?.telegram_id ? `${fromName} has no Telegram linked` : `Send nudge to ${fromName}`}
                        title={!fromUser?.telegram_id ? "Payer has no Telegram linked" : "Send nudge DM"}
                      >
                        {nudgingTxId === tx.id ? (
                          <div className="w-3 h-3 border-2 border-[#006855] border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <span aria-hidden="true" className="material-symbols-outlined text-base">send</span>
                        )}
                      </button>
                      <button
                        onClick={() => handleToggleSettled(tx.id, isSettledStatus ? 'settled' : 'pending')}
                        disabled={settlingTxId === tx.id}
                        className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-colors ${
                          isSettledStatus
                            ? 'bg-[#e8f5f0] text-[#006855] border-none'
                            : 'bg-[#f5f7f8] text-[#707977] border border-[#e5e9ea] hover:bg-[#e8f5f0] hover:text-[#006855] hover:border-[#00685540]'
                        }`}
                        aria-label={isSettledStatus ? `Mark ${fromName} to ${toName} transfer unsettled` : `Mark ${fromName} to ${toName} transfer settled`}
                        title={isSettledStatus ? "Mark Unsettled" : "Mark Settled"}
                      >
                        {settlingTxId === tx.id ? (
                          <div className="w-3 h-3 border-2 border-[#707977] border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <span aria-hidden="true" className="material-symbols-outlined text-base">{isSettledStatus ? 'check_circle' : 'circle'}</span>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {calculationLog.players.length > 0 && (
        <div className="bg-white rounded-xl mb-4 border border-[#e5e9ea] mt-6 opacity-80 overflow-hidden">
          <button
            type="button"
            onClick={() => setBreakdownExpanded((prev) => !prev)}
            className="flex w-full items-center justify-between px-3.5 py-2.5 text-[10px] font-bold uppercase tracking-wider text-[#595c5d] transition-colors hover:bg-[#f8fafb] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#006855]/30"
            aria-expanded={breakdownExpanded}
            aria-controls="digital-balance-breakdown"
          >
            <span>Digital Balance Breakdown</span>
            <span className={`transition-transform duration-300 ${breakdownExpanded ? 'rotate-180' : ''}`} aria-hidden="true">▼</span>
          </button>
          <div
            id="digital-balance-breakdown"
            className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
              breakdownExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="px-3.5 pb-3.5">
              {calculationLog.players
                .filter(p => Math.abs(p.settlement) > 0.01)
                .map((p, i, arr) => (
                  <div key={p.name} className={`flex justify-between items-center py-1.5 border-b border-[#f0f2f3] ${i === arr.length - 1 ? 'border-b-0' : ''}`}>
                    <span className="text-[13px] font-semibold text-[#2c2f30]">
                      {p.name}
                      {p.left_early && (
                        <span className="ml-2 text-[10px] font-bold uppercase text-[#b31b25] bg-[#b31b25]/10 px-1.5 py-0.5 rounded">Left Early</span>
                      )}
                    </span>
                    <span className={`text-sm font-extrabold ${p.settlement > 0 ? 'text-[#006855]' : 'text-[#b31b25]'}`}>
                      {p.settlement > 0 ? '+' : ''}{formatCurrency(p.settlement)}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

       {isAdmin && (
        <div className="fixed w-full max-w-[400px] left-1/2 -translate-x-1/2 px-4 py-3 bg-transparent z-10" style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom))' }}>
          <div className="max-w-[400px] mx-auto flex flex-col items-center gap-3">
            <button
              onClick={handleGameSettled}
              disabled={finalizing || (displayTransactions.length > 0 && !allSettled)}
              style={{
                width: '70%',
                height: 44,
                background: 'linear-gradient(135deg, #008a6d, #006855)',
                color: 'white',
                border: 'none',
                borderRadius: 22,
                fontWeight: 'bold',
                textTransform: 'uppercase',
                fontSize: 13,
                cursor: (finalizing || (displayTransactions.length > 0 && !allSettled)) ? 'not-allowed' : 'pointer',
                boxShadow: '0 4px 10px rgba(0,104,85,0.3)',
                opacity: (finalizing || (displayTransactions.length > 0 && !allSettled)) ? 0.3 : 1,
              }}
            >
              {finalizing ? 'SETTLING...' : 'GAME SETTLED'}
            </button>
            <button
              onClick={onBack}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#8a8d8e',
                fontSize: 11,
                fontWeight: 400,
                textDecoration: 'underline',
                cursor: 'pointer',
                paddingBottom: 8,
              }}
            >
              ← Back to Cash Distribution
            </button>
          </div>
        </div>
      )}

      <Modal isOpen={showLog} onClose={() => setShowLog(false)} title="Settlement Breakdown" maxWidth="max-w-[900px]">
        <div className="bg-white overflow-hidden flex flex-col max-h-[90vh]">
          <div className="p-4 sm:p-6 bg-[#f8fafb] border-b border-aura-text/5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <div className="text-[10px] font-black uppercase tracking-[1.5px] text-aura-text/40 mb-1">Total Game Pot</div>
              <div className="text-2xl font-black text-aura-primary tracking-tight">{formatCurrency(calculationLog.pot)}</div>
            </div>
            <div className="w-full sm:w-auto text-left sm:text-right">
              <div className="text-[10px] font-black uppercase tracking-[1.5px] text-aura-text/40 mb-1">Source Breakdown</div>
              <div className="flex gap-4 justify-start sm:justify-end">
                <div>
                  <span className="text-[10px] font-bold text-aura-text/40 mr-1">CASH</span>
                  <span className="text-sm font-bold text-aura-text">{formatCurrency(calculationLog.cash)}</span>
                </div>
                <div>
                  <span className="text-[10px] font-bold text-aura-text/40 mr-1">DIGITAL</span>
                  <span className="text-sm font-bold text-aura-text">{formatCurrency(calculationLog.digital)}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex-1 overflow-auto p-6 bg-white min-h-0">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse min-w-[800px]">
                <thead>
                  <tr className="bg-[#f8fafb] border-b border-[#eef1f2]">
                    <th className="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-aura-text/40">Player</th>
                    <th className="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-aura-text/40 text-right">Total Buy-in</th>
                    <th className="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-aura-text/40 text-right">Cash In</th>
                    <th className="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-aura-text/40 text-right">Digital In</th>
                    <th className="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-aura-text/40 text-right">Chips</th>
                    <th className="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-aura-text/40 text-right">Net Profit</th>
                    <th className="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-aura-text/40 text-right">Cash Recv</th>
                    <th className="px-4 py-3 text-[10px] font-black uppercase tracking-widest text-aura-text/40 text-right bg-aura-primary/5">Digital Owed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f5f7f8]">
                  {calculationLog.players.map((p, i) => (
                    <tr key={i} className="hover:bg-[#fcfdfe] transition-colors">
                      <td className="px-4 py-3 text-sm font-bold text-aura-text">
                        {p.name}
                        {p.left_early && (
                          <span className="ml-2 text-[10px] font-bold uppercase text-[#b31b25] bg-[#b31b25]/10 px-1.5 py-0.5 rounded">Left Early</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-right text-aura-text/70">{formatCurrency(p.totalBoughtIn)}</td>
                      <td className="px-4 py-3 text-sm font-medium text-right text-aura-text/60">{formatCurrency(p.cashIn)}</td>
                      <td className="px-4 py-3 text-sm font-medium text-right text-aura-text/60">{formatCurrency(p.digitalIn)}</td>
                      <td className="px-4 py-3 text-sm font-medium text-right text-aura-text/70">{formatCurrency(p.stack)}</td>
                      <td className={`px-4 py-3 text-sm font-black text-right ${p.profit > 0 ? 'text-aura-primary' : p.profit < 0 ? 'text-error' : 'text-aura-text/40'}`}>
                        {p.profit > 0 ? '+' : ''}{formatCurrency(p.profit)}
                      </td>
                       <td className="px-4 py-3 text-sm font-medium text-right text-aura-text/60">{formatCurrency(p.cash_received)}</td>
                      <td className={`px-4 py-3 text-sm font-black text-right bg-aura-primary/5 ${p.settlement > 0 ? 'text-aura-primary' : p.settlement < 0 ? 'text-error' : 'text-aura-text/40'}`}>
                        {p.settlement > 0 ? '+' : ''}{formatCurrency(p.settlement)}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-aura-primary/5 font-black">
                    <td className="px-4 py-4 text-[10px] uppercase tracking-widest text-aura-primary">Totals</td>
                    <td className="px-4 py-4 text-sm text-right text-aura-primary">{formatCurrency(calculationLog.players.reduce((s, p) => s + p.totalBoughtIn, 0))}</td>
                    <td className="px-4 py-4 text-sm text-right text-aura-primary">{formatCurrency(calculationLog.players.reduce((s, p) => s + p.cashIn, 0))}</td>
                    <td className="px-4 py-4 text-sm text-right text-aura-primary">{formatCurrency(calculationLog.players.reduce((s, p) => s + p.digitalIn, 0))}</td>
                    <td className="px-4 py-4 text-sm text-right text-aura-primary">{formatCurrency(calculationLog.players.reduce((s, p) => s + p.stack, 0))}</td>
                    <td className="px-4 py-4 text-sm text-right text-aura-primary">0.00</td>
                     <td className="px-4 py-4 text-sm text-right text-aura-primary">{formatCurrency(calculationLog.players.reduce((s, p) => s + p.cash_received, 0))}</td>
                    <td className="px-4 py-4 text-sm text-right text-aura-primary bg-aura-primary/10">{formatCurrency(calculationLog.players.reduce((s, p) => s + p.settlement, 0))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <div className="mt-6 sm:mt-8 p-4 sm:p-5 bg-[#f8fafb] rounded-2xl border border-[#eef1f2]">
              <div className="text-[10px] font-black uppercase tracking-[1.5px] text-aura-primary mb-2">Calculation Logic</div>
              <div className="text-[12px] font-bold text-aura-text/70 italic leading-relaxed">
                Formula: <span className="text-aura-primary">Digital Owed = Profit + Cash In - Cash Received</span>
              </div>
              <p className="mt-2 text-[11px] text-aura-text/50 font-medium leading-relaxed">
                This formula ensures that any physical cash a player put on the table that wasn't taken back during the cash distribution is automatically reimbursed through the digital transfers.
              </p>
            </div>
          </div>
          
          <div className="p-4 sm:p-6 border-t border-aura-text/5 flex justify-end bg-[#f8fafb]">
            <button 
              onClick={() => setShowLog(false)}
              className="w-full sm:w-auto px-8 py-3 bg-aura-primary text-white text-[13px] font-black uppercase tracking-widest rounded-full shadow-lg shadow-aura-primary/20 active:scale-95 transition-transform"
            >
              Close Breakdown
            </button>
          </div>
        </div>
      </Modal>

    </div>
  );
}
